/**
 * The automation tab's status: loaded once, re-read every 15 seconds while the
 * tab is visible, and on demand after an action.
 *
 * The automation ticks every 30 seconds on the server, so a page left open is
 * how an operator watches it work; the poll is what makes the page true without
 * a refresh button being pressed. It stops while the browser tab is hidden (a
 * page nobody looks at should not ask the server for anything) and reads once
 * the moment the tab is shown again, since everything that happened meanwhile
 * was missed.
 *
 * A failed poll keeps the last status on screen and raises `stale` instead of
 * blanking the page: the cards still say what was true a moment ago, and a
 * manager can still reach "stop everything". Only a first load that failed is
 * an error page. The reads go through `statusReader.ts`: the newest answer
 * wins, the poll skips while a read runs, and a refresh after an action always
 * reads for itself.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { AUTOMATION_POLL_MS, getAiAutomation, type AutomationStatus } from "../../../../lib/aiAutomation";
import { createStatusReader, type StatusReader } from "./statusReader";

export function useAiAutomation() {
  const [status, setStatus] = useState<AutomationStatus | null>(null);
  const [failed, setFailed] = useState(false);
  const reader = useRef<StatusReader | null>(null);
  if (reader.current === null) {
    reader.current = createStatusReader(getAiAutomation, (result) => {
      // A body this page cannot draw counts as a failed read, not as an empty state.
      const next = result.ok ? result.value : null;
      if (next) setStatus(next);
      setFailed(next === null);
    });
  }

  const load = useCallback(() => reader.current?.refresh() ?? Promise.resolve(), []);
  const poll = useCallback(() => reader.current?.poll() ?? Promise.resolve(), []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (timer === null) timer = setInterval(() => void poll(), AUTOMATION_POLL_MS);
    };
    const stop = () => {
      if (timer !== null) clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void poll();
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [poll]);

  return {
    status,
    /** The first load failed: nothing to draw. */
    error: failed && status === null,
    /** A later read failed: the status on screen is the last good one. */
    stale: failed && status !== null,
    refresh: load,
  };
}
